import { useEffect, useRef, useState } from 'react';
import { motion, useInView, animate } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { DowakumMark } from '@/components/BrandDecor';

function CountUp({ to, duration = 2.4, suffix = '' }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.22, 1.15, 0.32, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [inView, to, duration]);

  return (
    <motion.span
      ref={ref}
      className="tabular-nums inline-block"
      initial={{ y: 24, opacity: 0, scale: 0.92 }}
      animate={inView ? { y: 0, opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
    >
      {Math.round(display)}{suffix}
    </motion.span>
  );
}

const stats = [
  { key: 'products', to: 20,  suffix: '+' },
  { key: 'clinics',  to: 500, suffix: '+' },
  { key: 'years',    to: 10,  suffix: '+' },
];

export default function About() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-white py-24 md:py-40 overflow-hidden"
    >
      {/* Subtle brand mark watermark */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none hidden md:block absolute -right-16 top-1/2 -translate-y-1/2"
        style={{ opacity: 0.07 }}
      >
        <DowakumMark size={520} />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-[14px] md:text-[15px] tracking-[0.32em] uppercase text-[#8948C3] mb-6 font-semibold" data-testid="about-eyebrow">
            {t('about.eyebrow')}
          </div>
          <h2
            className={`text-[#10092E] font-bold tracking-tight ${
              isAr
                ? 'font-ar text-4xl md:text-6xl leading-[1.4]'
                : 'font-en text-5xl md:text-7xl leading-[1.1]'
            }`}
            data-testid="about-heading"
          >
            <span className="block pb-2 md:pb-3 mb-4 md:mb-6">{t('about.heading')}</span>
            <span className="block pb-2 md:pb-3 dwk-gradient-text">{t('about.heading_2')}</span>
          </h2>
          <div className="hairline mt-10 mb-10 mx-auto" />
          <div className={`grid md:grid-cols-2 gap-8 text-[#10092E]/70 leading-relaxed max-w-3xl mx-auto text-center ${isAr ? 'font-ar text-lg md:text-2xl' : 'font-en text-lg md:text-2xl'}`}>
            <p>{t('about.body_1')}</p>
            <p>{t('about.body_2')}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10 mt-20 md:mt-28 max-w-5xl mx-auto" data-testid="about-stats">
          {stats.map((s, i) => (
            <motion.div
              key={s.key}
              className="group relative rounded-2xl p-6 md:p-8 border border-[#10092E]/8 bg-[#F7F8FC]/60 hover:bg-white transition-colors"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`stat-${s.key}`}
            >
              <div className="text-5xl md:text-6xl font-bold tracking-tight font-en" style={{ color: '#8948C3' }}>
                <CountUp to={s.to} suffix={s.suffix} />
              </div>
              <div className={`mt-3 text-sm md:text-base ${isAr ? 'font-ar' : 'font-en'}`} style={{ color: '#8948C3' }}>
                {t(`about.stats.${s.key}`)}
              </div>
              <div className="absolute inset-x-6 bottom-4 h-[1px] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500 dwk-gradient" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
